import { H3 } from "~/ui-rtk/components/ui/typography"
import {
  ElementType,
  createElement,
  createRef,
  useCallback,
  useMemo,
} from "react"
import { type DashboardVisualizationDto } from "~/ui-rtk/api/defaultApi"
import useSources from "~/ui-rtk/hooks/sources"
import {
  AreaChart,
  CohortVisualization,
  ComposedChart,
  DropDownChart,
  TableVisualization,
  BarChart,
} from "../../../ui/charts"
import { type TGraphProps } from "../../../ui/charts/ComposedChart/ComposedChart"
import SimpleTable from "~/ui-rtk/components/ui/charts/SimpleTable/SimpleTable"
import SortedItems from "~/ui-rtk/components/ui/charts/SortedItems/SortedItems"
import MultiChart from "../../../ui/charts/MultiChart"
import ChartLoader from "../../../ui/charts/ChartLoader"
import { AggregationType, VISUALIZATION_TYPE } from "~/ui-rtk/constants/charts"
import {
  TDashboardItem,
  TDashboardItemWidget,
  TDashboardItemsSet,
  WIDGET_TYPE,
} from "~/ui-rtk/constants/dashboard"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { ExclamationSvg } from "~/ui-rtk/components/ui/svg/essentials"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import { flatContentWidgets } from "~/ui-rtk/utils/content"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { ComposedTable } from "../../../ui/charts/ComposedTable/ComposedTable"
import Card from "../../../ui/common/Card"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import {
  type TLastUpdateDateProps,
  useLastUpdateDate,
} from "~/ui-rtk/hooks/cube"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import useMobileView from "~/ui-rtk/hooks/mobile-view"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import { type TFilterColumnConfig } from "~/ui-rtk/components/ui/controls/TableFilters/types"
import { type TManualFilter } from "~/ui-rtk/components/ui/charts/TableVisualization/constants"
import { RichTooltip } from "~/ui-rtk/components/ui/common"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import useAggregationGroups from "~/ui-rtk/hooks/aggregation-groups"
import MultiSelectWithChips from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import { useUrlParamsStateArray } from "~/ui-rtk/hooks/urlParamsState"
const dayjs = useDate()

export interface TDashboardProps {
  title: string
  slug: string
  periodPicker?: { visible: boolean }
  items: TDashboardItemsSet
  excludeDataKeyFromTotal?: Array<string>
  parent: {
    slug: string
    title: string
  }
  visualizations: {
    [k: string]: DashboardVisualizationDto
  }
  dateRangeRestrictions?: Partial<{ minDate: Date; maxDate: Date }>
  cubeQueryParams?: Record<string, string | string[]>
  lastDateChipProps?: TLastUpdateDateProps
  props?: Record<string, unknown>
  toggleAmazon?: () => void
  amazonEnabled?: boolean
}

const VISUALIZATIONS: Record<VISUALIZATION_TYPE, ElementType> = {
  TableVisualization,
  SimpleTable,
  AreaChart,
  BarChart,
  ComposedChart,
  DropDownChart,
  CohortVisualization,
  MultiChart,
  ComposedTable,
  SortedItems,
}

const WIDGETS: Record<WIDGET_TYPE, ElementType> = {
  H3,
  SPACER: () => null,
}

const colsClassNameMap: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
}

const alignMap: Record<string, string> = {
  end: "items-end",
  center: "items-center",
  start: "items-start",
}

const justifyMap: Record<string, string> = {
  end: "justify-end",
  center: "justify-center",
  start: "justify-start",
}

const REVENUE_METRICS_FILTER_PARAM = "rm"

export default function Dashboard({
  title,
  items,
  slug,
  parent,
  visualizations,
  excludeDataKeyFromTotal,
  dateRangeRestrictions,
  cubeQueryParams = {},
  lastDateChipProps,
  toggleAmazon,
  amazonEnabled,
  props: contentProps = {},
}: TDashboardProps) {
  const { sources } = useSources()
  const isMobile = useMobileView()

  // Dynamically extract revenue metrics from visualizations with filterableDataKey
  const revenueMetrics = useMemo((): TMultiSelectOption[] => {
    const filterableWidget = Object.values(visualizations).find(
      (widget: DashboardVisualizationDto) =>
        widget?.props?.filterableDataKey === true,
    )

    if (!filterableWidget?.props?.graphs) {
      return []
    }

    return (
      filterableWidget.props.graphs as {
        label: string
        dataKey: string
        color: string
      }[]
    ).map((graph: { label: string; dataKey: string; color: string }) => ({
      label: graph.label,
      value: graph.dataKey,
      color: graph.color,
    }))
  }, [visualizations])

  let lastDate: string | undefined
  let isLastUpdateDateLoading = false
  if (lastDateChipProps) {
    const { dateMetric, filterMetrics } = lastDateChipProps
    const { lastDate: lastDateRaw, isLoading: isLastUpdateDateLoadingRaw } =
      useLastUpdateDate({
        dateMetric,
        filterMetrics,
      })
    lastDate = lastDateRaw
    isLastUpdateDateLoading = isLastUpdateDateLoadingRaw
  }
  const { dateRange, compareRange } = useMinMaxDateRange(
    dateRangeRestrictions?.minDate,
    dateRangeRestrictions?.maxDate ??
      (lastDate ? new Date(lastDate) : undefined),
  )
  const { granularity, setPeriod, setComparePeriod, setGranularity } =
    usePeriod()

  const { commonGranularityPicker } = contentProps as {
    commonGranularityPicker?: boolean
  }

  const plainItems: TDashboardItemWidget[] = flatContentWidgets(items)
  const visibleWidgets: (VISUALIZATION_TYPE | WIDGET_TYPE)[] = useMemo(
    () =>
      plainItems
        .filter(
          ({ widget }) =>
            !visualizations[widget]?.requiredSources?.length ||
            visualizations[widget]?.requiredSources?.some(source =>
              sources?.has(source),
            ),
        )
        .map(({ widget }) => widget),
    [items],
  )
  const refs = useMemo(
    () =>
      Object.fromEntries(
        visibleWidgets.map(id => [id, createRef<HTMLDivElement>()]),
      ),
    [visibleWidgets],
  )
  const showComparisonInPicker = plainItems.some(
    ({ widget }) => visualizations[widget]?.props?.comparisonEnabled,
  )
  const aggregationGroups = useAggregationGroups()

  // Check if any visible widget has filterable data keys
  const hasFilterableDataKey = useMemo(
    () =>
      visibleWidgets.some(
        (widget: string) => visualizations[widget]?.props?.filterableDataKey,
      ),
    [visibleWidgets, visualizations],
  )

  const {
    state: selectedRevenueMetricsKeys,
    setState: setSelectedRevenueMetricsKeys,
  } = useUrlParamsStateArray<Array<string>>(
    REVENUE_METRICS_FILTER_PARAM,
    revenueMetrics.map(m => m.value),
  )

  const selectedRevenueMetrics = useMemo(
    () =>
      revenueMetrics.filter(({ value }) =>
        (selectedRevenueMetricsKeys as string[]).includes(value),
      ),
    [selectedRevenueMetricsKeys, revenueMetrics],
  )

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const isSourceConnected = (widget: string) =>
    !visualizations[widget] ||
    (visualizations[widget]?.requiredSources?.length ?? 0) === 0 ||
    visualizations[widget]?.requiredSources?.some(source => sources.has(source))

  const handleSetDataAggType = (aggType: AggregationType) => {
    setGranularity(aggType)
  }

  const renderChartLoader = useCallback(
    (item: TDashboardItemWidget, itemsSet: TDashboardItem) => {
      const widgetType = visualizations[item.widget].type as VISUALIZATION_TYPE
      const tableTypes = [
        VISUALIZATION_TYPE.TableVisualization,
        VISUALIZATION_TYPE.ComposedTable,
        VISUALIZATION_TYPE.SimpleTable,
      ]

      // Filter graphs based on selected revenue metrics (visual filtering only, no new query)
      const selectedMetricDataKeys = selectedRevenueMetrics.map(m => m.value)

      const isFilterableChart =
        visualizations[item.widget]?.props?.filterableDataKey

      const filteredGraphs = isFilterableChart
        ? (
            visualizations[item.widget].props?.graphs as
              | { dataKey: string }[]
              | undefined
          )?.filter((graph: { dataKey: string }) =>
            selectedMetricDataKeys.includes(graph.dataKey),
          )
        : (visualizations[item.widget].props?.graphs as
            | TGraphProps[]
            | undefined)

      const chartLoader = (
        <ChartLoader
          chartId={item.widget}
          className={cn(itemsSet.className)}
          widgetType={widgetType}
          component={VISUALIZATIONS[widgetType]}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
          excludeDataKeyFromTotal={excludeDataKeyFromTotal}
          key={item.widget}
          granularity={granularity}
          sources={sources}
          hideAggSelector={commonGranularityPicker as boolean | undefined}
          requiredSources={visualizations[item.widget]?.requiredSources}
          setDataAggType={handleSetDataAggType}
          cubeQueryParams={{
            [item.widget]: {
              ...cubeQueryParams,
              granularity,
            },
          }}
          description={visualizations[item.widget]?.description}
          compareMode={
            (visualizations[item.widget].props.comparisonEnabled as boolean) &&
            (compareRange?.length ?? 0) > 0
          }
          // TEMP SOLUTION
          {...visualizations[item.widget].props}
          graphs={filteredGraphs}
        />
      )

      if (widgetType === VISUALIZATION_TYPE.TableVisualization) {
        return (
          <div>
            <TableFiltersBar
              id={item.widget}
              columnConfig={
                visualizations[item.widget].props
                  .columnConfig as TFilterColumnConfig[]
              }
              additionalFilters={
                visualizations[item.widget].props.additionalFilters as
                  | TManualFilter[]
                  | undefined
              }
            />
            <div className="mt-3">{chartLoader}</div>
          </div>
        )
      }

      if (tableTypes.includes(widgetType)) {
        return chartLoader
      }

      return (
        <Card rounded className="bg-basic-dark-blue">
          {chartLoader}
        </Card>
      )
    },
    [
      visualizations,
      cubeQueryParams,
      amazonEnabled,
      sources,
      granularity,
      dateRange,
      compareRange,
      excludeDataKeyFromTotal,
      selectedRevenueMetrics,
    ],
  )

  const info = useMemo(() => {
    const renderInfoWidget = visibleWidgets.find(
      (widget: string) => !!visualizations[widget]?.renderInfo,
    )
    if (renderInfoWidget) {
      const renderInfo = visualizations[renderInfoWidget]?.renderInfo
      return (renderInfo && renderInfo()) ?? undefined
    }
  }, [visibleWidgets, visualizations])

  return (
    <div>
      <div
        className={cn({
          "flex items-start justify-between flex-col ": !isMobile,
        })}
      >
        <div className="flex justify-start items-center text-start w-full">
          <Breadcrumbs
            items={[
              {
                slug: parent.slug,
                name: parent.title,
              },
              {
                slug,
                name: title,
              },
            ]}
          />
          {info && (
            <RichTooltip
              content={
                <Card className="p-0 relative overflow-hidden flex items-center justify-center bg-transparent border-none cursor-pointer">
                  {info}
                </Card>
              }
            >
              <InformationCircleIcon className="w-system-md h-system-md ml-system-sm cursor-pointer" />
            </RichTooltip>
          )}
        </div>
      </div>
      <div className="flex justify-between items-stretch gap-system-sm mb-system-lg">
        {hasFilterableDataKey && (
          <div className="w-full">
            <MultiSelectWithChips
              options={revenueMetrics}
              showTypeAhead
              value={selectedRevenueMetrics}
              onChange={(options: TMultiSelectOption[]) =>
                setSelectedRevenueMetricsKeys(options.map(({ value }) => value))
              }
            />
          </div>
        )}
        <div
          className={cn(
            "flex gap-system-xs items-stretch",
            hasFilterableDataKey ? "justify-end" : "justify-end w-full",
          )}
        >
          {toggleAmazon && (
            <AmazonToggle
              amazonEnabled={amazonEnabled}
              toggleAmazon={toggleAmazon}
            />
          )}
          {commonGranularityPicker ? (
            <div className="join h-full">
              {aggregationGroups.length > 1
                ? aggregationGroups.map(({ type, long }) => (
                    <Button
                      variant={{ variant: "solid", color: "blue" }}
                      key={type}
                      title={long}
                      className={cn(
                        `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm  rounded-system-md`,
                        { "bg-system-blue-800": type !== granularity },
                        {
                          "bg-system-blue-600": type === granularity,
                        },
                      )}
                      onClick={() => handleSetDataAggType(type)}
                    >
                      {long}
                    </Button>
                  ))
                : null}
            </div>
          ) : null}
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                granularity={granularity}
                compareStart={
                  compareRange?.[0] && isValidDate(compareRange[0])
                    ? compareRange[0]
                    : undefined
                }
                comparisonEnabled={showComparisonInPicker}
                compareEnd={
                  compareRange?.[1] && isValidDate(compareRange[1])
                    ? compareRange[1]
                    : undefined
                }
                onChange={handleChangePeriods}
              />
            )}
          {lastDate && (
            <RichTooltip
              content={
                <LastUpdatedChip
                  usedInTooltip={true}
                  lastDate={lastDate}
                  isLoading={isLastUpdateDateLoading}
                >
                  Max date available in Page:
                </LastUpdatedChip>
              }
            >
              <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
                <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
                  <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
                </span>
                <InformationCircleIcon className="w-system-lg h-system-lg" />
              </Card>
            </RichTooltip>
          )}
        </div>
      </div>
      <div className="grid-stack">
        {items.length > 0 ? (
          items.map((itemsSet: TDashboardItem, idx: number) => {
            const filteredWidgets = itemsSet.items.filter(
              (item: TDashboardItemWidget) =>
                visibleWidgets.includes(item.widget),
            )

            return filteredWidgets.length > 0 ? (
              <div
                className={`grid grid-stack-item ${colsClassNameMap[itemsSet.grid]} gap-4 my-4`}
                key={`grid-stack-item-${idx}`}
              >
                {filteredWidgets.map((item, idy) => (
                  <div
                    ref={refs[item.widget]}
                    key={`${item.widget}-${idx}-${idy}`}
                    className={`grid-stack-item-content ${["SPACER", "H3"].includes(item.widget) ? "overflow-hidden" : ""}`}
                  >
                    <div className="relative">
                      {!isSourceConnected(item.widget) ? (
                        <div className="absolute top-0 bottom-0 left-0 right-0 flex justify-center gap-1 align-center z-1">
                          <ExclamationSvg size={20} />
                        </div>
                      ) : null}
                      {visualizations[item.widget] &&
                      VISUALIZATIONS[
                        visualizations[item.widget].type as VISUALIZATION_TYPE
                      ]
                        ? renderChartLoader(item, itemsSet)
                        : null}
                      {WIDGETS[item.widget as WIDGET_TYPE] ? (
                        <div
                          className={`w-100 h-full flex ${item.verticalAlign && alignMap[item.verticalAlign]} ${item.horizontalAlign && justifyMap[item.horizontalAlign]}`}
                        >
                          {createElement(WIDGETS[item.widget as WIDGET_TYPE], {
                            children: item.text,
                          })}
                        </div>
                      ) : null}
                      {!visualizations[item.widget] &&
                      !WIDGETS[item.widget as WIDGET_TYPE] ? (
                        <div className="flex items-center justify-center h-full bg-background-dark w-100">
                          <ExclamationSvg size={32} fill="error" />
                          <span className="font-bold text-error">
                            Invalid visualization "{item.widget}"
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          })
        ) : (
          <div>This page is under construction. Please come again later</div>
        )}
      </div>
    </div>
  )
}
